import { BASE } from './api.js';
import type { Faceta, RecuentoFacetado } from './tipos.js';

/** Lo que devuelven las tres lecturas públicas del catálogo (`plan/PLAN.md` § 2.1). */

export interface FuenteDelCatalogo {
  readonly slug: string;
  readonly tituloOriginal: string;
  readonly editor: string;
  readonly anio: number | null;
  readonly pais: string | null;
  readonly tipoPoblacion: string | null;
  readonly muestra: string | null;
  readonly urlDocumento: string | null;
  readonly resumen: string | null;
  readonly portadaUrl?: string | null;
  readonly anadidoEn?: string | null;
}

export interface Catalogo {
  readonly fuentes: readonly FuenteDelCatalogo[];
  readonly recuentos: Readonly<Record<Faceta, readonly RecuentoFacetado[]>>;
}

export interface Resumen {
  readonly fuentes: number;
  readonly hechosVigentes: number;
  /** Estudios con al menos un hecho vigente. Opcional: una API anterior no lo trae. */
  readonly fuentesConHechos?: number;
  readonly corpusVersion: number;
}

/**
 * Cuántos estudios se anuncian: los que tienen datos de verdad (hechos vigentes), no los del
 * catálogo entero, que incluye estudios aún sin datos (144 frente a ~127 el 2026-09-29).
 */
export const estudiosConDatos = (r: Resumen): number => r.fuentesConHechos ?? r.fuentes;

/**
 * Las lecturas del catálogo son `GET` y no cuestan modelos, así que no gastan cuota ni tope.
 * Van igualmente con la cookie: es la misma sesión, y así el API puede contar el uso real.
 */
async function leer<T>(ruta: string): Promise<T | null> {
  try {
    const r = await fetch(`${BASE}${ruta}`, { credentials: 'include' });
    if (!r.ok) return null;
    return (await r.json()) as T;
  } catch {
    return null;
  }
}

export const pedirCatalogo = (): Promise<Catalogo | null> => leer<Catalogo>('/corpus/fuentes');
export const pedirResumen = (): Promise<Resumen | null> => leer<Resumen>('/corpus/resumen');
export const pedirFicha = (slug: string): Promise<unknown> => leer(`/corpus/fuentes/${encodeURIComponent(slug)}`);

/** Un dato citado de una fuente, tal como lo da `GET /corpus/fuentes/:slug/citas`. */
export interface CitaDeFicha {
  readonly id: string;
  readonly pagina: number | null;
  readonly origen: 'texto' | 'tabla' | 'grafico';
  readonly cifra: string | null;
  readonly cita: string | null;
  readonly afirmacion: string;
}

/** El dato al que enlaza alguien (`hecho`) o los de una página (`pagina`); `null` si no está o falla. */
export async function pedirCitas(
  slug: string,
  filtro: { readonly pagina: number } | { readonly hecho: string },
): Promise<readonly CitaDeFicha[] | null> {
  const consulta = 'pagina' in filtro ? `pagina=${String(filtro.pagina)}` : `hecho=${encodeURIComponent(filtro.hecho)}`;
  const r = await leer<{ citas: readonly CitaDeFicha[] }>(`/corpus/fuentes/${encodeURIComponent(slug)}/citas?${consulta}`);
  return r?.citas ?? null;
}
